import { X } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { AsyncMultiSelect } from "../asyncSelect/AsyncMultiSelect";
import { AsyncSingleSelect } from "../asyncSelect/AsyncSingleSelect";
import DateInput from "../customFormFields/DateInput";
import { FormSelectSingle, SelectMulti } from "../customFormFields/FormSelect";

import DateDropdownFilter from "./DateDropdownFilter";
import DatePickerWithRange from "./DatePickerWithRange";
import DateRangePickerWithPresets from "./DateRangePickerWithPresets";
import { applyClearFilterParam, getDateRangeMatchers } from "./filter.utils";
import type { FilterType } from "./filters.types";
import YearSliderFilter from "./YearSliderFilter";

import type { LabelValue } from "@/app/app.interface";
import type { useQueryFilterType } from "@/hooks/query/useQueryParams";
import type { Any } from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

type FilterSide = "top" | "right" | "bottom" | "left";

export interface FilterEntryComponentProps<T = Any, P = Any> {
  filterKey: string;
  value: Any;
  side?: FilterSide;
  filters: FilterType<T, P>;
  filterOperation: useQueryFilterType;
}

const NOOP_HANDLER = (): undefined => undefined;

const getFilterConfig = <T, P>(
  props: FilterEntryComponentProps<T, P>,
): Exclude<FilterType<T, P>[string], boolean> => {
  return props.value as Exclude<FilterType<T, P>[string], boolean>;
};

const getLinkedFilterKey = <T, P>(
  filters: FilterType<T, P>,
  filterKey: string,
): string | undefined => {
  const filterConfig = filters[filterKey];

  return typeof filterConfig === "boolean" ? undefined : filterConfig.linked;
};

const getFortnightDates = (
  getAllValue: useQueryFilterType["getAllValue"],
): Date[] => {
  const fortnightValues = getAllValue("fortnight");

  return fortnightValues && fortnightValues.length === 2
    ? [new Date(fortnightValues[0]), new Date(fortnightValues[1])]
    : [];
};

/**
 * Renders a single date filter that writes its selected date to URL params.
 *
 * @param {FilterEntryComponentProps} props - Date filter rendering context.
 * @returns {JSX.Element} Date filter input.
 */
export const DateFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { filterOperation, filters, filterKey } = props;
  const filterConfig = filters[filterKey];
  const placeholder =
    typeof filterConfig === "object" ? filterConfig.placeholder : undefined;

  return (
    <DateInput
      key='date'
      onChange={(date: Date | undefined) => {
        if (date) {
          filterOperation.updateParams("date", date.toString());
        }
      }}
      placeholder={placeholder}
      className='w-fit dashed-border'
    />
  );
};

/**
 * Renders a date range filter that writes start and end values to URL params.
 *
 * @param {FilterEntryComponentProps} props - Date range filter rendering context.
 * @returns {JSX.Element} Date range filter input.
 */
export const DateRangeFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { filterKey, side, filterOperation } = props;
  const { clearParam, getAllValue, replaceSameKeys } = filterOperation;
  const { dateRangeMatcher, dateRangeValues, dateRangeVal } =
    getDateRangeMatchers(getAllValue);

  return (
    <DatePickerWithRange
      key='dateRange'
      side={side}
      className={cn(
        "[&>button]:w-fit dashed-border h-10",
        side === "right" && "[&>button]:w-[180px]",
      )}
      defaultValue={dateRangeVal}
      matcher={dateRangeMatcher}
      onChange={(
        date: DateRange | undefined | React.FormEvent<HTMLDivElement>,
      ) => {
        const selectedDateRange = date as DateRange;

        if (selectedDateRange?.from && selectedDateRange?.to) {
          replaceSameKeys("dateRange", [
            selectedDateRange.from.toDateString(),
            selectedDateRange.to.toDateString(),
          ]);

          return;
        }

        clearParam("dateRange");
      }}
      {...(dateRangeValues?.length && {
        customIcon: (
          <X
            className='ml-4 h-4 w-4 opacity-50 hover:text-red-500'
            onClick={(event) => {
              event.stopPropagation();
              clearParam(filterKey);
            }}
          />
        ),
      })}
    />
  );
};

/**
 * Renders a date range filter with preset selectors.
 *
 * @param {FilterEntryComponentProps} props - Date range preset rendering context.
 * @returns {JSX.Element} Date range preset filter input.
 */
export const DateRangeWithPresetsFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { value, side, filterOperation } = props;
  const { getAllValue, mutateParams } = filterOperation;
  const { dateRangeMatcher, dateTypeValues, dateRangeVal } =
    getDateRangeMatchers(getAllValue);

  return (
    <DateRangePickerWithPresets
      key='DateRangePickerWithPresets'
      side={side}
      className={cn(
        "[&>button]:w-fit dashed-border h-10",
        side === "right" && "[&>button]:w-[180px]",
      )}
      value={dateRangeVal}
      matcher={dateRangeMatcher}
      multiSelection={{
        options: value?.dateRangePresetsConfig?.options,
        selectedOptions: dateTypeValues,
        setSelectedOptions: () => undefined,
      }}
      disableFutureDates={value?.disableFutureDates}
      onApply={(date, selectedDateTypes) => {
        mutateParams((nextParams) => {
          nextParams.delete("dateRange");
          if (date?.from && date?.to) {
            nextParams.append("dateRange", date.from.toDateString());
            nextParams.append("dateRange", date.to.toDateString());
          }

          nextParams.delete("dateType");
          selectedDateTypes.forEach((type) => {
            if (type) {
              nextParams.append("dateType", type);
            }
          });
        });
      }}
    />
  );
};

/**
 * Renders a date dropdown filter using the filter key as the URL param.
 *
 * @param {FilterEntryComponentProps} props - Date dropdown rendering context.
 * @returns {JSX.Element} Date dropdown filter input.
 */
export const DateDropdownFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { filterKey, value, side, filterOperation } = props;
  const filterConfig = getFilterConfig(props);
  const { clearParam, getAllValue, updateParams } = filterOperation;
  const selectedDate = getAllValue(filterKey)?.[0] || "";

  return (
    <DateDropdownFilter
      key={`${filterKey}-date-dropdown`}
      label={filterConfig?.label || "Date"}
      placeholder={
        filterConfig?.label ? `Select ${filterConfig.label}` : "Select Date"
      }
      selectedDate={selectedDate}
      onDateChange={(date: string | null) => {
        clearParam(filterKey);

        if (date) {
          updateParams(filterKey, date);
        }
      }}
      fortnightDates={getFortnightDates(getAllValue)}
      side={side}
      className={cn(
        value === true
          ? "hover:bg-secondary min-w-44 max-w-48"
          : "hover:bg-secondary w-48",
        side !== "right" && "dashed-border",
      )}
    />
  );
};

/**
 * Renders a sliding year selector.
 *
 * @param {FilterEntryComponentProps} props - YearSliderFilter rendering context.
 * @returns {JSX.Element} YearSlider filter selector.
 */
export const YearSliderFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { value } = props;

  return (
    <YearSliderFilter
      forwardYearOffset={value?.forwardYearOffset}
      className='rounded-md py-0.5'
    />
  );
};

/**
 * Renders a static multi-select filter.
 *
 * @param {FilterEntryComponentProps} props - Multi-select rendering context.
 * @returns {JSX.Element} Multi-select filter input.
 */
export const MultiSelectFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { filterKey, value, side, filterOperation } = props;
  const { replaceSameKeys, getAllValue } = filterOperation;
  const selectedValues = getAllValue(filterKey) || [];

  return (
    <SelectMulti
      key={`${filterKey}-multi-select`}
      side={side}
      withoutform={{
        values: selectedValues,
        onValueChange: (
          selectedOptions: { label: string; value: string }[],
        ) => {
          replaceSameKeys(
            filterKey,
            selectedOptions?.map((selectedOption) => selectedOption.value) ??
              [],
          );
        },
      }}
      displayLabel={value?.label}
      options={value?.options as LabelValue[]}
      maxCount={1}
      placeholder={value?.label ? `Select ${value.label}` : `${filterKey}`}
      className={cn(
        "hover:bg-secondary w-64 pl-4",
        side !== "right" && "dashed-border",
      )}
      isPopoverContentWidthFixed={false}
      isFilter={true}
    />
  );
};

/**
 * Renders an async paginated multi-select filter.
 *
 * @param {FilterEntryComponentProps} props - Async multi-select rendering context.
 * @returns {JSX.Element} Async multi-select filter input.
 */
export const PaginatedMultiSelectFilterEntry = (
  props: FilterEntryComponentProps,
): JSX.Element => {
  const { filterKey, value, side, filterOperation } = props;
  const { mutateParams, getAllValue } = filterOperation;
  const selectedValues = getAllValue(filterKey) || [];
  const labelParamKey = `${filterKey}_label`;
  const defaultLabels = getAllValue(labelParamKey) || [];

  return (
    <AsyncMultiSelect
      key={`${filterKey}-multi-select`}
      useQuery={value?.paginatedSelectConfig?.useQuery}
      label={value?.label || filterKey}
      values={selectedValues}
      isFilter
      defaultLabels={defaultLabels.map((label: string, index: number) => ({
        label,
        value: selectedValues[index],
      }))}
      onChange={(selectedValues, _selectedObjects, selectedLabels) => {
        mutateParams((nextParams) => {
          nextParams.delete(filterKey);
          nextParams.delete(labelParamKey);
          selectedValues.forEach((selectedValue: string) => {
            if (selectedValue) {
              nextParams.append(filterKey, selectedValue);
            }
          });
          selectedLabels?.forEach((selectedLabel: string) => {
            if (selectedLabel) {
              nextParams.append(labelParamKey, selectedLabel);
            }
          });
        });
      }}
      renderConfig={value?.paginatedSelectConfig?.renderConfig}
      baseParams={value?.paginatedSelectConfig?.baseQuery}
      placeholder={value?.label ? `Select ${value.label}` : `${filterKey}`}
      triggerClassName={cn(
        "hover:bg-secondary w-64 h-10",
        side !== "right" && "dashed-border",
        value?.className,
      )}
      side={side}
      maxSelectedDisplay={1}
    />
  );
};

/**
 * Renders an async paginated single-select filter.
 *
 * @param {FilterEntryComponentProps} props - Async single-select rendering context.
 * @returns {JSX.Element} Async single-select filter input.
 */
export const PaginatedSingleSelectFilterEntry = <T, P>(
  props: FilterEntryComponentProps<T, P>,
): JSX.Element => {
  const { filterKey, value, side, filters, filterOperation } = props;
  const { mutateParams, getAllValue } = filterOperation;
  const selectedValue = getAllValue(filterKey)?.[0] || "";
  const labelParamKey = `${filterKey}_label`;
  const defaultLabel = getAllValue(labelParamKey)?.[0] || "";

  return (
    <AsyncSingleSelect
      key={`${filterKey}-single-select`}
      useQuery={value?.paginatedSelectConfig?.useQuery}
      label={value?.label || filterKey}
      onChange={(selectedValue, _selectedObject, selectedLabel) => {
        const linkedFilterKey = getLinkedFilterKey(filters, filterKey);

        mutateParams((nextParams) => {
          nextParams.delete(filterKey);
          nextParams.delete(labelParamKey);

          if (linkedFilterKey) {
            nextParams.delete(linkedFilterKey);
            nextParams.delete(`${linkedFilterKey}_label`);
          }

          if (!selectedValue) {
            return;
          }

          nextParams.set(filterKey, selectedValue);

          if (selectedLabel) {
            nextParams.append(labelParamKey, selectedLabel);
          }
        });
      }}
      renderConfig={value?.paginatedSelectConfig?.renderConfig}
      baseParams={value?.paginatedSelectConfig?.baseQuery}
      side={side}
      showArrow={!selectedValue.length}
      value={selectedValue}
      defaultLabel={defaultLabel}
      placeholder={value?.label ? ` Select ${value.label}` : `${filterKey} `}
      triggerClassName={cn(
        "hover:bg-secondary min-w-44 max-w-48",
        side !== "right" && " dashed-border",
      )}
      clearableConfig={{
        isClearable: true,
        onClear: () => {
          mutateParams((nextParams) => {
            nextParams.delete(filterKey);
            nextParams.delete(labelParamKey);
          });
        },
      }}
    />
  );
};

/**
 * Renders a static single-select filter.
 *
 * @param {FilterEntryComponentProps} props - Single-select rendering context.
 * @returns {JSX.Element} Single-select filter input.
 */
export const SingleSelectFilterEntry = <T, P>(
  props: FilterEntryComponentProps<T, P>,
): JSX.Element => {
  const { filterKey, value, side, filters, filterOperation } = props;
  const { keys, getAllValue, mutateParams } = filterOperation;
  const labelParamKey = `${filterKey}_label`;
  const selectedLabelFromUrl = getAllValue(labelParamKey)?.[0] || "";
  const defaultLabel = selectedLabelFromUrl || value?.defaultLabel || "";

  return (
    <FormSelectSingle
      key={`${filterKey}-single-select`}
      withoutform
      side={side}
      isShowArrow={!keys[filterKey]}
      defaultLabel={defaultLabel}
      field={{
        name: filterKey,
        onBlur: NOOP_HANDLER,
        onChange: (selectedValue: string) => {
          const linkedFilterKey = getLinkedFilterKey(filters, filterKey);
          const isClearSelection = selectedValue === "ALL" || !selectedValue;
          const selectedOptionLabel = value?.options?.find(
            (option: { id: string; label: string }) =>
              option.id === selectedValue,
          )?.label;

          mutateParams(
            (nextParams) => {
              if (isClearSelection) {
                applyClearFilterParam(nextParams, filterKey, linkedFilterKey);
              } else {
                nextParams.set(filterKey, selectedValue);
                nextParams.delete(labelParamKey);

                if (selectedOptionLabel) {
                  nextParams.set(labelParamKey, selectedOptionLabel);
                }
              }

              if (linkedFilterKey && !isClearSelection) {
                nextParams.delete(linkedFilterKey);
                nextParams.delete(`${linkedFilterKey}_label`);
              }
            },
            { replace: isClearSelection },
          );
        },
        ref: NOOP_HANDLER,
        value: keys ? keys[filterKey] : "",
      }}
      options={value?.options?.map((option: { id: string; label: string }) => ({
        label: option.label,
        value: option.id,
      }))}
      placeholder={value?.label ? ` ${value.label}` : `${filterKey} `}
      className={cn(
        "hover:bg-secondary w-48 h-10",
        side !== "right" && " dashed-border",
      )}
      popoverClassName='max-w-[250px]'
      clearableConfig={{
        isClearable: true,
        onClear: () => {
          const linkedFilterKey = getLinkedFilterKey(filters, filterKey);

          mutateParams(
            (nextParams) => {
              applyClearFilterParam(nextParams, filterKey, linkedFilterKey);
            },
            { replace: true },
          );
        },
      }}
    />
  );
};
